<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import DOMPurify from "dompurify";
import axios from "@/axios";
import { useAuthStore } from "@/stores/auth";
import Swal from "sweetalert2";
import RegistrationForm from "@/components/auth/RegistrationForm.vue";
import AuthLayout from "@/components/layouts/AuthLayout.vue";
import { APP_VERSION } from "@/constants";
const router = useRouter();
const authStore = useAuthStore();
const isLoading = ref(false);
const formRef = ref(null);

// Toast personalizado para notificaciones
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
  },
  customClass: {
    popup: "rounded-xl border border-[#99b642]/30",
    title: "text-[#4a5c28]",
  },
});

// Función de sanitización de datos
const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;
  return DOMPurify.sanitize(input.trim());
};

// Mostrar sugerencias de validación al usuario
const showValidationHints = () => {
  Toast.fire({
    icon: "info",
    title: "Recomendaciones para el formulario",
    html: `
      <ul class="text-left text-sm">
        <li>• Tu contraseña debe tener al menos 8 caracteres</li>
        <li>• Incluye al menos una letra y un número</li>
        <li>• Usa un correo electrónico válido</li>
      </ul>
    `,
  });
};

// Manejar el envío del formulario - CORREGIDO
const onSubmit = async (formValues) => {
  try {
    isLoading.value = true;

    console.log("Formulario enviado con valores:", formValues); // Para debugging

    // Mostrar preloader con SweetAlert2
    Swal.fire({
      title: "Procesando tu registro",
      html: "Esto solo tomará un momento...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
      background: "rgba(255, 255, 255, 0.95)",
      backdrop: "rgba(74, 92, 40, 0.4)",
      customClass: {
        title: "text-[#4a5c28]",
        popup: "rounded-xl border border-[#99b642]/30",
      },
    });

    // Sanitizar los datos de entrada
    const sanitizedValues = {
      name: sanitizeInput(formValues.name),
      email: sanitizeInput(formValues.email.toLowerCase()),
      phone: formValues.phone.replace(/\D/g, ""),
      shirt_size: formValues.shirt_size,
      password: formValues.password, // No sanitizamos contraseñas
      password_confirmation: formValues.passwordConfirmation,
      birthdate: formValues.birthdate,
    };

    // Preparar los datos para el registro usando el prefijo correcto
    const registrationData = {
      name: sanitizedValues.name,
      email: sanitizedValues.email,
      phone: `${formValues.selectedPrefix}${sanitizedValues.phone}`,
      country_code: formValues.country_code || 'gt', // Guatemala por defecto
      birthdate: sanitizedValues.birthdate,
      shirt_size: sanitizedValues.shirt_size,
      password: sanitizedValues.password,
      password_confirmation: sanitizedValues.password_confirmation,
    };

    // Enviar datos al servidor
    const response = await axios.post("/v1/register", registrationData);

    // Cerrar el preloader
    Swal.close();

    // Si el registro es exitoso
    if (response.data && response.data.token) {
      // Guardar token y datos de usuario
      authStore.setToken(response.data.token);
      authStore.setUser(response.data.user);

      // Mostrar mensaje de éxito con animación personalizada
      Swal.fire({
        icon: "success",
        title: "¡Registro exitoso!",
        text: "Tu cuenta ha sido creada correctamente",
        timer: 2000,
        showConfirmButton: false,
        background: "rgba(255, 255, 255, 0.95)",
        backdrop: `
          rgba(74, 92, 40, 0.7)
          url('/img/auth/confetti.gif')
        `,
        customClass: {
          title: "text-[#4a5c28]",
          popup: "rounded-xl border border-[#99b642]/30",
        },
        showClass: {
          popup: "animate__animated animate__fadeInUp animate__faster",
        },
        hideClass: {
          popup: "animate__animated animate__fadeOutDown animate__faster",
        },
      });

      // Redireccionar al usuario a la selección de país
      setTimeout(() => {
        router.push("/onboarding");
      }, 2200);
    }
  } catch (error) {
    console.error("Error en registro:", error);

    // Cerrar el preloader
    Swal.close();

    // Mostrar mensaje de error según la respuesta del servidor
    let errorMessage = "Ha ocurrido un error al registrarse";
    let errorDetails = "";

    if (error.response) {
      if (error.response.data && error.response.data.errors) {
        const serverErrors = error.response.data.errors;

        if (
          serverErrors.email &&
          serverErrors.email.includes("already been taken")
        ) {
          errorMessage = "Este correo electrónico ya está registrado";
          errorDetails = "Intenta iniciar sesión o recuperar tu contraseña";

          // Mostrar como toast para emails duplicados
          Toast.fire({
            icon: "error",
            title: errorMessage,
            text: errorDetails,
          });
          return;
        } else {
          errorMessage = "Por favor verifica la información ingresada";
          errorDetails = Object.values(serverErrors)[0][0] || "";
        }
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
      errorDetails = "Verifica tu conexión a internet e intenta nuevamente";
    }

    // Mostrar error con SweetAlert
    Swal.fire({
      icon: "error",
      title: errorMessage,
      text: errorDetails,
      confirmButtonText: "Intentar nuevamente",
      confirmButtonColor: "#99b642",
      background: "rgba(255, 255, 255, 0.95)",
      customClass: {
        confirmButton: "bg-[#99b642] text-white px-4 py-2 rounded-lg",
        title: "text-red-600",
        popup: "rounded-xl border border-red-100",
      },
    });
  } finally {
    isLoading.value = false;
  }
};

// Mostrar las sugerencias de validación al cargar la página
onMounted(() => {
  setTimeout(showValidationHints, 500);
});
</script>

<template>
  <AuthLayout>
    <template #title>
      <h1 class="text-4xl font-extrabold text-white mb-2 drop-shadow-lg">
        Regístrate
      </h1>
      <p class="text-white/80 text-lg mb-6 max-w-md mx-auto">
        Crea tu cuenta para comenzar tu aventura con nosotros
      </p>
    </template>

    <!-- Component RegistrationForm CORREGIDO -->
    <RegistrationForm ref="formRef" @submit="onSubmit">
      <template #button-content>
        <span v-if="isLoading" class="flex items-center justify-center">
          <div
            class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
          ></div>
          Procesando...
        </span>
        <span v-else class="flex items-center justify-center">
          <i class="ri-user-add-line mr-2 text-xl"></i>
          Crear mi cuenta
        </span>
      </template>
    </RegistrationForm>
    
    <div class="max-w-md mx-auto space-y-6">
 <!--      <SocialLoginButtons
        mode="register"
        @google-auth-success="handleAuthSuccess"
      /> -->
    </div>

    <template #footer-links>
      <p class="text-center text-white mt-6">
        ¿Ya tienes cuenta?
        <router-link
          to="/login"
          class="font-semibold underline hover:text-[#d4e4a9] transition-colors ml-1"
        >
          Inicia sesión aquí
        </router-link>
      </p>
      <p class="text-center text-white mt-6">
        ¿Tienes problemas para registrarte?
        <a
          href="mailto:support@hikesocietycam.com"
          class="font-semibold underline hover:text-[#d4e4a9] transition-colors ml-1"
        >
            Escríbenos a support@hikesocietycam.com
        </a>
      </p>
      <p class="text-center text-white mt-6">
        Versión {{ APP_VERSION }}
      </p>
    </template>
  </AuthLayout>
</template>

<style scoped>
.loader-pulse {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #99b642;
  box-shadow: 0 0 0 0 rgba(153, 182, 66, 1);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(153, 182, 66, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 15px rgba(153, 182, 66, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(153, 182, 66, 0);
  }
}
</style>
